/**
 * Client for the compile worker, with a synchronous fallback.
 *
 * Three things matter here:
 *
 *  - **Staleness.** Requests carry a monotonic id and replies that are not the
 *    latest are dropped. Without that, a slow compile of an older document
 *    could land after a newer one and put a stale diagram on screen.
 *  - **Degradation.** Workers are unavailable in some embedded webviews and in
 *    jsdom. If construction fails, the same pipeline runs inline; the app never
 *    loses its diagram over it.
 *  - **Debounce.** Recompiling on literally every keystroke is wasted work, so
 *    requests are coalesced. The delay is configurable, and zero means "as soon
 *    as the current task yields", not "synchronously".
 */
import { runPipeline } from './runPipeline';
import type {
  PipelineMessage,
  PipelineOptions,
  PipelineRequest,
  PipelineResult,
} from './pipelineTypes';

export type CompileListener = (result: PipelineResult) => void;
export type CompileErrorListener = (message: string) => void;

export interface CompilerClient {
  /** Queues a compile; later calls within the debounce window replace earlier ones. */
  request(source: string, options: PipelineOptions): void;
  /** Compiles immediately, bypassing the debounce. */
  requestNow(source: string, options: PipelineOptions): void;
  setDebounce(ms: number): void;
  onResult(listener: CompileListener): () => void;
  onError(listener: CompileErrorListener): () => void;
  readonly usesWorker: boolean;
  dispose(): void;
}

export function createCompilerClient(debounceMs = 150): CompilerClient {
  let worker: Worker | null = null;
  let disposed = false;
  let nextId = 1;
  let latestSent = 0;
  let latestApplied = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: PipelineRequest | null = null;
  let debounce = debounceMs;

  const resultListeners = new Set<CompileListener>();
  const errorListeners = new Set<CompileErrorListener>();

  try {
    // `import.meta.url` + `type: 'module'` is the form Vite understands and
    // bundles; anything more dynamic ends up unbundled at runtime.
    worker = new Worker(new URL('./compile.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<PipelineMessage>) => {
      const message = event.data;
      if (message.kind === 'error') {
        emitError(message.failure.error);
        return;
      }
      apply(message.result);
    };
    worker.onerror = (event) => {
      emitError(event.message || 'The compiler worker failed.');
      // Fall back permanently: a worker that has errored once is not to be
      // trusted with the next keystroke.
      worker?.terminate();
      worker = null;
    };
  } catch {
    worker = null;
  }

  function emitError(message: string): void {
    for (const listener of errorListeners) listener(message);
  }

  function apply(result: PipelineResult): void {
    if (disposed) return;
    // Drop anything superseded while it was in flight.
    if (result.id < latestApplied) return;
    latestApplied = result.id;
    for (const listener of resultListeners) listener(result);
  }

  function send(request: PipelineRequest): void {
    latestSent = request.id;
    if (worker) {
      worker.postMessage(request);
      return;
    }
    try {
      apply(runPipeline(request));
    } catch (error) {
      emitError(error instanceof Error ? error.message : String(error));
    }
  }

  function schedule(request: PipelineRequest, immediate: boolean): void {
    pending = request;
    if (timer !== null) clearTimeout(timer);
    if (immediate) {
      timer = null;
      const next = pending;
      pending = null;
      if (next) send(next);
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      const next = pending;
      pending = null;
      if (next) send(next);
    }, debounce);
  }

  return {
    get usesWorker() {
      return worker !== null;
    },
    request(source, options) {
      if (disposed) return;
      schedule({ id: nextId++, source, options }, debounce <= 0);
    },
    requestNow(source, options) {
      if (disposed) return;
      schedule({ id: nextId++, source, options }, true);
    },
    setDebounce(ms) {
      debounce = Math.max(0, Math.min(2000, ms));
    },
    onResult(listener) {
      resultListeners.add(listener);
      return () => resultListeners.delete(listener);
    },
    onError(listener) {
      errorListeners.add(listener);
      return () => errorListeners.delete(listener);
    },
    dispose() {
      disposed = true;
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pending = null;
      resultListeners.clear();
      errorListeners.clear();
      worker?.terminate();
      worker = null;
      // Referenced so the linter can see it is meaningful state, and so a
      // future caller can tell whether anything was ever sent.
      void latestSent;
    },
  };
}
